import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dialog", "fileInput", "feedback", "addButton"];
  static values = { previewUrl: String };

  openModal(event) {
    event.preventDefault();
    this.clearFeedback();
    this.dialogTarget.showModal();
  }

  closeModal(event) {
    if (event) event.preventDefault();
    this.dialogTarget.close();
  }

  async import(event) {
    event.preventDefault();

    const file = this.fileInputTarget.files?.[0];

    if (!file) {
      this.setError("Selecione um arquivo CSV para importar.");
      return;
    }

    const formData = new FormData();
    formData.append("import_products[file]", file);

    this.setInfo("Importando produtos...");

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        this.setError(
          (data.errors || ["Erro ao processar a importação."]).join(" "),
        );
        return;
      }

      if (!Array.isArray(data.items) || data.items.length === 0) {
        const errorText = (
          data.errors || ["Nenhum produto válido encontrado no arquivo."]
        ).join(" ");
        this.setError(errorText);
        return;
      }

      await this.appendItemsToForm(data.items);

      const warningText =
        Array.isArray(data.errors) && data.errors.length > 0
          ? ` Avisos: ${data.errors.join(" ")}`
          : "";
      this.setInfo(
        `${data.items.length} produto(s) adicionado(s) ao carrinho (rascunho).${warningText}`,
      );
      this.closeModal();
    } catch (_) {
      this.setError("Erro ao importar produtos. Tente novamente.");
    }
  }

  async appendItemsToForm(items) {
    for (const item of items) {
      this.addButtonTarget.click();
      await this.nextFrame();

      const wrappers = this.element.querySelectorAll(".nested-form-wrapper");
      const wrapper = wrappers[wrappers.length - 1];
      if (!wrapper) continue;

      const orderProductElement = wrapper.querySelector(
        '[data-controller~="order-product"]',
      );
      const quantityInput = wrapper.querySelector('input[name*="[quantity]"]');
      const unitValueInput = wrapper.querySelector(
        'input[name*="[unit_value]"]',
      );
      const suggestedValueInput = wrapper.querySelector(
        'input[name*="[suggested_value]"]',
      );
      const totalValueInput = wrapper.querySelector(
        'input[name*="[total_value]"]',
      );

      if (orderProductElement) {
        orderProductElement.dispatchEvent(
          new CustomEvent("cart-import:set-product", {
            bubbles: true,
            detail: {
              productId: item.product_id,
              productLabel: item.product_label,
              unitValue: item.unit_value,
            },
          }),
        );
      }

      if (unitValueInput)
        unitValueInput.value = this.formatDecimal(item.unit_value);
      if (suggestedValueInput)
        suggestedValueInput.value = this.formatDecimal(item.suggested_value);
      if (quantityInput) quantityInput.value = String(item.quantity);
      if (totalValueInput)
        totalValueInput.value = this.formatDecimal(item.total_value);

      if (quantityInput) {
        quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  }

  setInfo(message) {
    this.feedbackTarget.classList.remove("text-red-500");
    this.feedbackTarget.classList.add("text-gray-500");
    this.feedbackTarget.textContent = message;
  }

  setError(message) {
    this.feedbackTarget.classList.remove("text-gray-500");
    this.feedbackTarget.classList.add("text-red-500");
    this.feedbackTarget.textContent = message;
  }

  clearFeedback() {
    this.feedbackTarget.textContent = "";
    this.feedbackTarget.classList.remove("text-red-500");
    this.feedbackTarget.classList.add("text-gray-500");
  }

  formatDecimal(value) {
    const number = Number.parseFloat(value || 0);
    return Number.isNaN(number) ? "0.00" : number.toFixed(2);
  }

  csrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""
    );
  }

  nextFrame() {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }
}
